export const AVAILABILITY_STATUSES = {
  IN_STOCK: 'IN_STOCK',
  SOLD_OUT: 'SOLD_OUT',
  MADE_TO_ORDER: 'MADE_TO_ORDER',
  VINTAGE: 'VINTAGE',
  ENQUIRE: 'ENQUIRE',
}

// Products synced from our custom-sales sheet are flagged customSales=true in
// Typesense (products-v5). They must not appear in public product listings or
// search, so every listing query appends this filter. Uses `!=true` (rather
// than `=false`) so regular products — which carry customSales=false — are
// kept; only the explicitly-flagged ones are excluded.
export const EXCLUDE_CUSTOM_SALES_FILTER = 'customSales:!=true'

export const CUSTOMIZATION_TYPES = {
  COM: 'COM',
  CUSTOM_DIMENSIONS: 'CUSTOM_DIMENSIONS',
  CUSTOM_FINISHES: 'CUSTOM_FINISHES',
}

export const ITEM_TYPES = {
  NEW_AND_MADE_TO_ORDER: 'NEW_AND_MADE_TO_ORDER',
  VINTAGE: 'VINTAGE',
}

export const LOCATION_TYPES = {
  DOMESTIC: 'DOMESTIC',
  INTERNATIONAL: 'INTERNATIONAL',
}

export const FABRIC_TYPES = {
  COTTON: 'COTTON',
  LINEN: 'LINEN',
  POLYESTER: 'POLYESTER',
  PERFORMANCE: 'PERFORMANCE',
  VELVET: 'VELVET',
  CHENILLE: 'CHENILLE',
  SILK: 'SILK',
  WOOL: 'WOOL',
}

export const AVAILABILITY_STATUSES_TRANSLATIONS = {
  [AVAILABILITY_STATUSES.IN_STOCK]: 'In stock',
  [AVAILABILITY_STATUSES.SOLD_OUT]: 'Sold out',
  [AVAILABILITY_STATUSES.MADE_TO_ORDER]: 'Made to order',
  [AVAILABILITY_STATUSES.VINTAGE]: 'Vintage',
  [AVAILABILITY_STATUSES.ENQUIRE]: 'Enquire',
} as const

export const CUSTOMIZATION_TYPES_TRANSLATIONS = {
  [CUSTOMIZATION_TYPES.COM]: `Customer's own material`,
  [CUSTOMIZATION_TYPES.CUSTOM_DIMENSIONS]: 'Custom dimensions',
  [CUSTOMIZATION_TYPES.CUSTOM_FINISHES]: 'Custom finishes',
} as const

export const ITEM_TYPES_TRANSLATIONS = {
  [ITEM_TYPES.NEW_AND_MADE_TO_ORDER]: 'New and made to order',
  [ITEM_TYPES.VINTAGE]: 'Vintage',
} as const

export const LOCATION_TYPES_TRANSLATIONS = {
  [LOCATION_TYPES.DOMESTIC]: 'Domestic',
  [LOCATION_TYPES.INTERNATIONAL]: 'International',
} as const

export const FABRIC_TYPES_TRANSLATIONS = {
  [FABRIC_TYPES.COTTON]: 'Cotton',
  [FABRIC_TYPES.LINEN]: 'Linen',
  [FABRIC_TYPES.POLYESTER]: 'Polyester',
  [FABRIC_TYPES.PERFORMANCE]: 'Performance',
  [FABRIC_TYPES.VELVET]: 'Velvet',
  [FABRIC_TYPES.CHENILLE]: 'Chenille',
  [FABRIC_TYPES.SILK]: 'Silk',
  [FABRIC_TYPES.WOOL]: 'Wool',
} as const

export const INSTANT_SEARCH_TRANSLATIONS: Record<string, string> = {
  ...AVAILABILITY_STATUSES_TRANSLATIONS,
  ...CUSTOMIZATION_TYPES_TRANSLATIONS,
  ...ITEM_TYPES_TRANSLATIONS,
  ...LOCATION_TYPES_TRANSLATIONS,
  ...FABRIC_TYPES_TRANSLATIONS,
}

const TYPESENSE_ATTRIBUTES = {
  minPrice: 'minPrice',
  maxPrice: 'maxPrice',
  minTradePrice: 'minTradePrice',
  maxTradePrice: 'maxTradePrice',
  minTradeDiscount: 'minTradeDiscount',
  maxTradeDiscount: 'maxTradeDiscount',
  isInStock: 'isInStock',
  leadTimesInWeeks: 'leadTimesInWeeks',
  consultationsPrice: 'consultations.price',
  consultationsMiddleOfSlotUtcTimestamps:
    'consultations.middleOfSlotUtcTimestamps',
  consultationsLengthInMinutes: 'consultations.lengthInMinutes',
  styles: 'styles',
  regionsLvl0: 'regions.lvl0',
  widths: 'widths',
  heights: 'heights',
  depths: 'depths',
  lengths: 'lengths',
  isTopExpert: 'isTopExpert',
}

// Display-only relabeling of consultation lengths: sessions are stored with a
// 5-minute buffer (30/60/90) but are sold as 25/55/85-minute consultations.
// The underlying facet values and refinements are untouched.
const CONSULTATION_LENGTH_DISPLAY_LABELS: Record<string, string> = {
  '30': '25',
  '60': '55',
  '90': '85',
}

export const formatUtc = (timestamp: number) => {
  const date = new Date(timestamp * 1000)
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
}

export function translateRefinementLabel({
  value,
  suffix,
  attribute,
  realValue,
  operator,
}: {
  value: string
  suffix?: string
  attribute?: string
  realValue?: string | number
  operator?: string
}): string {
  if (value.startsWith('NOBRAND-')) {
    return value.replace('NOBRAND-', '')
  }
  if (attribute === TYPESENSE_ATTRIBUTES.consultationsLengthInMinutes) {
    return `${CONSULTATION_LENGTH_DISPLAY_LABELS[value] ?? value}${suffix ?? ''}`
  }
  if (
    attribute === TYPESENSE_ATTRIBUTES.consultationsMiddleOfSlotUtcTimestamps &&
    realValue &&
    operator
  ) {
    return `${operator} ${formatUtc(Number(realValue))}`
  }
  return `${INSTANT_SEARCH_TRANSLATIONS[value] ?? ((value === 'true' || value === 'false' ? '' : value) as string)}${suffix ?? ''}`
}

export const TYPESENSE_ATTRIBUTES_TRANSLATIONS = {
  [TYPESENSE_ATTRIBUTES.minPrice]: 'Price ($):',
  [TYPESENSE_ATTRIBUTES.maxPrice]: 'Price ($):',
  [TYPESENSE_ATTRIBUTES.minTradePrice]: 'Trade price ($):',
  [TYPESENSE_ATTRIBUTES.maxTradePrice]: 'Trade price ($):',
  [TYPESENSE_ATTRIBUTES.minTradeDiscount]: 'Trade discount (%):',
  [TYPESENSE_ATTRIBUTES.maxTradeDiscount]: 'Trade discount (%):',
  [TYPESENSE_ATTRIBUTES.isInStock]: 'In stock',
  [TYPESENSE_ATTRIBUTES.leadTimesInWeeks]: 'Lead time (weeks):',
  [TYPESENSE_ATTRIBUTES.consultationsPrice]: 'Price ($):',
  [TYPESENSE_ATTRIBUTES.consultationsMiddleOfSlotUtcTimestamps]:
    'Availability:',
  [TYPESENSE_ATTRIBUTES.consultationsLengthInMinutes]: 'Length (minutes):',
  [TYPESENSE_ATTRIBUTES.styles]: 'Style:',
  [TYPESENSE_ATTRIBUTES.regionsLvl0]: 'Region:',
  [TYPESENSE_ATTRIBUTES.widths]: 'Width (inches):',
  [TYPESENSE_ATTRIBUTES.heights]: 'Height (inches):',
  [TYPESENSE_ATTRIBUTES.depths]: 'Depth (inches):',
  [TYPESENSE_ATTRIBUTES.lengths]: 'Length (inches):',
  [TYPESENSE_ATTRIBUTES.isTopExpert]: 'Top experts only',
}

export function translateTypesenseAttribute({
  value,
}: {
  value: string
}): string {
  return `${TYPESENSE_ATTRIBUTES_TRANSLATIONS[value] ?? ''}`
}

export const FILTERS = {
  WIDTH: 'WIDTH',
  HEIGHT: 'HEIGHT',
  DEPTH: 'DEPTH',
  LENGTH: 'LENGTH',
  AVAILABILITY: 'AVAILABILITY',
  FABRIC: 'FABRIC',
  LOCATION: 'LOCATION',
  ITEM_TYPE: 'ITEM_TYPE',
  CUSTOMIZATION: 'CUSTOMIZATION',
  TRADE_DISCOUNT: 'TRADE_DISCOUNT',
  PRICE: 'PRICE',
  LEAD_TIME: 'LEAD_TIME',
}

export const ALLOWED_FILTERS_FOR_CATEGORIES = {
  [FILTERS.WIDTH]: ['Furniture'],
  [FILTERS.HEIGHT]: ['Furniture'],
  [FILTERS.DEPTH]: ['Furniture'],
  [FILTERS.LENGTH]: ['Furniture'],
  [FILTERS.FABRIC]: ['Fabric & Wall Coverings'],
}
